import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import { getStatusByEntity } from "@/services/entity-status.service";
import {
  getNotificationTypeDetail,
  patchNotificationType,
  type NotificationTypeDetail,
  type PatchNotificationTypeData,
} from "@/services/notification-types.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import type { NavigateFunction } from "react-router-dom";

interface FetchNotificationTypeDetailHandlerArgs {
  id: number;
  setLoading: (v: boolean) => void;
  setDetail: (v: NotificationTypeDetail | null) => void;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

export const fetchNotificationTypeDetailHandler = async ({
  id,
  setLoading,
  setDetail,
  navigate,
  fetchPermissions,
  routeState,
}: FetchNotificationTypeDetailHandlerArgs) => {
  try {
    setLoading(true);
    const response = await getNotificationTypeDetail({ id });
    setDetail(response);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.NOTIFICATIONS,
      routeState,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo cargar el detalle."),
      });
    }

    throw error;
  } finally {
    setLoading(false);
  }
};

interface FetchEntityStatusNameArgs {
  idEntidad: number;
  idEstado: number;
}

export const fetchEntityStatusName = async ({
  idEntidad,
  idEstado,
}: FetchEntityStatusNameArgs): Promise<string | null> => {
  const response = await getStatusByEntity({ Id: idEntidad });
  const list = Array.isArray(response) ? response : [];
  const match = list.find((x) => (x?.ids ?? []).includes(idEstado));
  return match?.nombre ?? null;
};

interface UpdateNotificationTypeHandlerArgs {
  id: number;
  data: PatchNotificationTypeData;
  setLoading: (v: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

export const updateNotificationTypeHandler = async ({
  id,
  data,
  setLoading,
  navigate,
  fetchPermissions,
  routeState,
}: UpdateNotificationTypeHandlerArgs) => {
  try {
    setLoading(true);
    await patchNotificationType(id, data);
    toaster.create({
      type: "success",
      title: "Notificación actualizada con éxito",
    });

    navigate(AppRoutes.NOTIFICATIONS);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.NOTIFICATIONS,
      routeState,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo actualizar."),
      });
    }

    throw error;
  } finally {
    setLoading(false);
  }
};
